import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import { InviteUserService } from '@pages/first-time-onboarding/services/invite-user.service';
import { patch } from '@ngxs/store/operators';
import {
  CheckUserExistence,
  InviteUser,
  InviteUserCompleted,
  SelectOnboardingOption,
} from '@pages/first-time-onboarding/store/actions/invite-user.actions';
import { OnboardingOption } from '@pages/first-time-onboarding/models/onboarding-option.enum';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ToastService } from '@core/toast/toast.service';
import { nonFlickerLoader } from '@kaseya-utils';

interface InviteUserStateModel {
  userExists?: boolean;
  selectedOnboardingOption?: OnboardingOption;
  userExistsLoading: boolean;
  inviteUserLoading: boolean;
  userExistsError: boolean;
  inviteUserError: boolean;
}
@State<InviteUserStateModel>({
  name: 'inviteUserOnboarding',
  defaults: {
    userExistsLoading: false,
    userExistsError: false,
    inviteUserLoading: false,
    inviteUserError: false,
  },
})
@Injectable()
export class InviteUserState {
  private readonly inviteUserService = inject(InviteUserService);
  private readonly toastService = inject(ToastService);

  @Selector()
  static userExists(state: InviteUserStateModel): boolean | undefined {
    return state.userExists;
  }

  @Selector()
  static selectedOnboardingOption(state: InviteUserStateModel): OnboardingOption | undefined {
    return state.selectedOnboardingOption;
  }

  @Selector()
  static userExistsLoading(state: InviteUserStateModel): boolean {
    return state.userExistsLoading;
  }

  @Selector()
  static inviteUserLoading(state: InviteUserStateModel): boolean {
    return state.inviteUserLoading;
  }

  @Selector()
  static userExistsError(state: InviteUserStateModel): boolean {
    return state.userExistsError;
  }

  @Selector()
  static inviteUserError(state: InviteUserStateModel): boolean {
    return state.inviteUserError;
  }

  @Action(SelectOnboardingOption)
  selectOnboardingOption(context: StateContext<InviteUserStateModel>, { onboardingOption }: SelectOnboardingOption): void {
    context.setState(patch<InviteUserStateModel>({ selectedOnboardingOption: onboardingOption }));
  }

  @Action(CheckUserExistence, { cancelUncompleted: true })
  checkUserExistence(context: StateContext<InviteUserStateModel>, { userEmail }: CheckUserExistence): Observable<boolean | undefined> {
    return nonFlickerLoader(this.inviteUserService.doesUserExist(userEmail), 500).pipe(
      tap(userExists => {
        if (userExists === undefined) {
          context.patchState({ userExistsLoading: true });
          return;
        }

        context.patchState({
          userExists,
          userExistsLoading: false,
          userExistsError: false,
        });
      }),
      catchError(() => {
        context.patchState({
          userExistsError: true,
          userExistsLoading: false,
        });
        return EMPTY;
      })
    );
  }

  @Action(InviteUser)
  inviteUser(context: StateContext<InviteUserStateModel>, { user }: InviteUser): Observable<void> {
    if (context.getState().inviteUserLoading) {
      return EMPTY;
    }

    context.patchState({ inviteUserLoading: true });

    return this.inviteUserService.inviteUser(user).pipe(
      tap(() => context.dispatch(new InviteUserCompleted())),
      finalize(() => context.patchState({ inviteUserLoading: false })),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'EmailDomainNotAllowed') {
          this.toastService.showErrorToast('first-time-onboarding.toasts.domain-not-allowed');
          context.dispatch(new InviteUserCompleted());
        }

        return EMPTY;
      })
    );
  }
}
